import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CentrexExtensionModel } from 'src/app/models/centrex-extension.model';
import { CentrexExtensionsService } from 'src/app/services/centrex-extensions.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { UsersService } from 'src/app/services/users.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CentrexSdasService } from 'src/app/services/centrex-sdas.service';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { ExtensionsFormComponent } from './extensions-form/extensions-form.component';
import { AccessRightsService } from 'src/app/services/access-rights.service';
import { OrganizationsService } from 'src/app/services/organization.service';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { CentrexUsersService } from 'src/app/services/centrex-users-service';
import { CentrexSitesService } from 'src/app/services/centrex-sites.service';
import { PlatformService } from 'src/app/services/platform.service';

@Component({
  selector: 'app-extensions',
  templateUrl: './extensions.component.html',
  styleUrls: ['./extensions.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSelectModule,
    FormsModule,
    MatPaginator,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDialogModule,
    NgxSpinnerModule,
  ],
})
export class ExtensionsComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'extension',
    'name',
    'members',
    'siteId',
    'distribution',
    'sda',
    'active',
    'actions',
  ];
  dataSource = new MatTableDataSource<CentrexExtensionModel>();
  filtersForm!: FormGroup;

  organizationId: string | null = null;
  platform: string = this.platformService.getCurrentPlatform();

  allExtensions: CentrexExtensionModel[] = [];

  isFullAccess = false;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private spinnerService = inject(NgxSpinnerService);

  constructor(
    private centrexExtensionsService: CentrexExtensionsService,
    private usersService: UsersService,
    private fb: FormBuilder,
    private centrexSdasService: CentrexSdasService,
    private dialog: MatDialog,
    private accessRightsService: AccessRightsService,
    private organizationsService: OrganizationsService,
    private centrexUsersService: CentrexUsersService,
    private centrexSitesService: CentrexSitesService,
    private platformService: PlatformService,
  ) {}

  ngOnInit(): void {
    this.filtersForm = this.fb.group({
      extension: [''],
      name: [''],
      distribution: [''],
      sda: [''],
      members: [''],
      siteId: [''],
    });

    // Écoute des changements de droits
    this.accessRightsService
      .hasNexusCentrexFullAccess()
      .subscribe((isAllowed) => (this.isFullAccess = isAllowed));

    // Écoute des changements d'organisation
    this.organizationsService.currentOrganizationChange$.subscribe((org) => {
      if (org) {
        this.organizationId = org.id;
        this.loadExtensions();
      }
    });

    // // Chargement initial si org déjà en session
    // const currentOrg = this.organizationsService.getCurrentOrganization();
    // if (currentOrg) {
    //   this.organizationId = currentOrg.id;
    //   this.loadExtensions();
    // }
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onPlatformChange(): void {
    this.platformService.setPlatform(this.platform);

    this.loadExtensions();
  }

  loadExtensions(): void {
    if (!this.organizationId) return;

    this.spinnerService.show('spinner');

    if (this.platform === 'Centrex Enreach') {
      // NOUVEAU FLOW ENREACH
      forkJoin({
        extensions: this.centrexExtensionsService.getExtensions(
          this.organizationId,
          this.platform,
        ),
        groups: this.centrexExtensionsService.getExtensionGroups(
          this.organizationId,
          this.platform,
        ),
        sdas: this.centrexSdasService.getSdas(
          this.organizationId,
          this.platform,
        ),
        users: this.centrexUsersService.getAllCentrexUsers(
          // ICI
          this.organizationId!,
          this.platform,
        ),
        sites: this.centrexSitesService.getSites(
          // 👈 AJOUT ICI
          this.organizationId!,
          this.platform,
        ),
      })
        .pipe(finalize(() => this.spinnerService.hide('spinner')))
        .subscribe({
          next: ({ extensions, groups, sdas, users, sites }) => {
            this.allExtensions = extensions;

            // MAP pour lookup rapide
            const extensionMap = new Map(extensions.map((e) => [e.id, e]));

            const userMap = new Map(
              users.map((u: any) => [String(u.number).trim(), u]),
            );

            const siteMap = new Map(
              sites.map((s: any) => [String(s.siteID), s.name]),
            );
            const cleanGroups = groups
              .filter((g: any) => !g.name?.startsWith('~'))
              .map((group: any) => ({
                ...group,

                enabled: group.enabled ?? false,

                siteName: siteMap.get(String(group.siteId)) || '—',

                members: (group.members || []).map((m: any) => {
                  const number = m.number;
                  const full = extensionMap.get(m.id);

                  const user = userMap.get(String(number).trim());

                  return {
                    ...m,
                    number: number || '—',
                    name:
                      full?.name ||
                      (user
                        ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
                        : '—'),
                  };
                }),
              }));

            this.dataSource.data = cleanGroups;
            console.log('GROUPS FINAL FRONT =', cleanGroups);
            this.applyFilters();
          },
          error: (err) => {
            console.error('Erreur chargement Enreach', err);
          },
        });
    } else {
      // FLOW NG (inchangé)
      forkJoin({
        extensions: this.centrexExtensionsService.getExtensions(
          this.organizationId,
          this.platform,
        ),
        sdas: this.centrexSdasService.getSdas(
          this.organizationId,
          this.platform,
        ),
      })
        .pipe(finalize(() => this.spinnerService.hide('spinner')))
        .subscribe({
          next: ({ extensions, sdas }) => {
            this.allExtensions = extensions;

            this.enrichMembersWithUserInfo(this.allExtensions);

            const groups = this.allExtensions.filter(
              (ext) => ext.type === 'group' && !ext.name?.startsWith('~'),
            );

            groups.forEach((group) => {
              const sda = sdas.find((s) => s.target === group.id);
              (group as any).sdaNumber = sda?.number || '—';
              const siteIds = group.extensions
                ?.map(
                  (id) =>
                    this.allExtensions.find((ext) => ext.id === id)?.siteId,
                )
                .filter(Boolean);

              (group as any).siteId = siteIds?.length
                ? [...new Set(siteIds)].join(', ')
                : '—';
            });

            this.dataSource.data = groups;
            this.applyFilters();
          },
          error: (err) => {
            console.error('Erreur chargement NG', err);
          },
        });
    }
  }

  private enrichMembersWithUserInfo(extensions: CentrexExtensionModel[]): void {
    //this.spinnerService.show('spinner');
    //console.log('Extensions=' + JSON.stringify(extensions));
    const userExtensions = extensions.filter(
      (e) => e.type === 'user' && e.userId,
    );

    if (userExtensions.length === 0) return;
  }

  applyFilters(): void {
    const values = this.filtersForm.value;
    this.dataSource.filterPredicate = (data, filter) => {
      const terms = JSON.parse(filter);
      return Object.keys(terms).every((key) => {
        const value = (data as any)[key]?.toString()?.toLowerCase() || '';
        return !terms[key] || value.includes(terms[key].toLowerCase());
      });
    };
    this.dataSource.filter = JSON.stringify(values);
  }

  resetColumnFilter(column: string): void {
    this.filtersForm.get(column)?.setValue('');
    this.applyFilters();
  }

  getMemberNames(extension: any): string[] {
    if (this.platform === 'Centrex Enreach') {
      if (!extension.members || extension.members.length === 0) return ['—'];

      return extension.members.map((m: any) => {
        return `${m.number || '—'} – ${m.name || '—'}`;
      });
    }

    // NG
    if (!extension.extensions || extension.extensions.length === 0)
      return ['—'];

    return extension.extensions.map((id: string) => {
      const member = this.allExtensions.find((ext) => ext.id === id);
      if (!member) return this.formatId(id);

      return `${member.number || '—'} – ${member.name || '—'}`;
    });
  }

  private formatId(id: string): string {
    return id.substring(0, 7) + '...' + id.substring(id.length - 5);
  }

  onView(extension: any): void {
    let members: any[] = [];

    if (this.platform === 'Centrex Enreach') {
      members = extension.members || [];
    } else {
      members = this.allExtensions.filter((ext) =>
        extension.extensions?.includes(ext.id),
      );
    }

    this.dialog.open(ExtensionsFormComponent, {
      width: '900px',
      data: {
        extension,
        organizationId: this.organizationId!,
        platform: this.platform,
        mode: 'view',
        members, // ICI
      },
      disableClose: true,
    });
  }

  onEdit(extension: CentrexExtensionModel): void {
    if (!this.isFullAccess) return;

    const members = extension.members || [];

    this.dialog
      .open(ExtensionsFormComponent, {
        width: '900px',
        data: {
          extension,
          organizationId: this.organizationId!,
          platform: this.platform,
          mode: 'edit',
          members, // AJOUT ICI
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.updated) {
          this.loadExtensions();
        }
      });
  }

  getDialerTypeLabel(type: string): string {
    switch (type?.toLowerCase()) {
      case 'cyclic':
        return 'Cyclique';

      case 'sequential':
        return 'Séquentiel';

      case 'unisson':
        return 'Unisson';

      case 'oldestidle':
        return 'Plus ancien';

      default:
        return type || '—';
    }
  }
}
